/**
 * Error-classification predicates shared by the layered handlers
 * (`QueryCache.onError`, `QueryErrorBoundary`, route `errorComponent`,
 * `InlineQueryError`), kept here so the rules are testable across owners.
 *
 * See docs/guidelines/trpc-error-handling.md.
 */

import {
    isProtectedRoute,
    isSyntheticSessionExpiredError,
    isTrpcExecutionDeadlineError,
    isUnauthorizedTrpcError,
} from '@client/trpc/sessionHandling';
import { isAuthRoute } from '@client/utils/common/auth';
import { isNetworkError } from '@client/utils/errors/network-error-detection';
import {
    type CLAIM_AUTHORITY_DECISION_ERROR_CODES,
    CLAIM_AUTHORITY_MANAGER_ERROR_CODES,
    type CLAIM_AUTHORITY_RESERVE_CHANGE_ERROR_CODES,
    CONTACT_WRITE_DENIAL_MESSAGES,
    type CuriumErrorCode,
    PASSWORD_RESET_LINK_ERROR_CODES,
} from '@shared/constants';

import { isEdgeChallengeError } from './edge-response';
import { DEFAULT_QUERY_ERROR_SURFACE, type ErrorSurface, SURFACES_WITH_DOWNSTREAM_SENTRY_OWNER } from './query-meta';

// Re-exports so call sites don't import deep into other modules for the
// canonical predicates.
export { isEdgeChallengeError, isNetworkError, isSyntheticSessionExpiredError, isUnauthorizedTrpcError };

/**
 * Minimal shape of the auth store the predicates need. Accepting an interface
 * (rather than calling `useAuthStore.getState()` here) keeps these pure and
 * testable without the Zustand singleton.
 */
export interface AuthSessionSnapshot {
    /** `authTokenExpired` flag from the auth store. */
    readonly authTokenExpired: boolean;
    /** Live token expiry check (e.g. `useAuthStore.getState().isTokenExpired`). */
    readonly isTokenExpired: () => boolean;
    /**
     * `isLoggingOut` flag from the auth store: a deliberate logout (user menu, inactivity,
     * cross-tab) has cleared the token and is waiting on the Auth0 redirect.
     */
    readonly isLoggingOut: boolean;
}

/** The app code of a refused captcha challenge, the one UNAUTHORIZED the server owns (CUR-1962). */
const CAPTCHA_REFUSAL_ERROR_ID = 'captcha.rejected' satisfies CuriumErrorCode;

function hasMessage(error: unknown, message: string): boolean {
    return typeof error === 'object' && error !== null && 'message' in error && error.message === message;
}

const EXPECTED_CONTACT_WRITE_DENIAL_MESSAGES: ReadonlySet<string> = new Set(
    Object.values(CONTACT_WRITE_DENIAL_MESSAGES),
);

/**
 * The server's `recordNotFound` app code: the row-level access boundary answering a deleted
 * record, a stale link, or a record the caller may not read. The server drops it from Sentry and
 * keeps an Axiom signal; the client must not re-file it from the cache layer. Detail views also
 * match on it to render an unavailable state instead of a generic failure.
 */
export function isRecordNotFoundError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null || !('data' in error)) return false;
    const data = (error as { data?: unknown }).data;
    return typeof data === 'object' && data !== null && (data as { errorId?: unknown }).errorId === 'recordNotFound';
}

/**
 * True when the server's `clientCapture: false` marker settles who reports the error (CUR-1962).
 * The server sends it on a refusal it threw with `captureInSentry: false` and on every
 * application `NOT_FOUND`: it has already decided what Sentry hears, so the browser must not
 * file the error again. This is the general form of the rule the predicates below each spell out
 * for one refusal; they stay because several also drive UI, and they cover an older server.
 *
 * One exception: an UNAUTHORIZED the server marked is the browser's to judge unless it is a
 * refused captcha challenge (`captcha.rejected`). The server keeps its session gate out of
 * Sentry, but only the browser knows whether it should have held a session, and a request that
 * left without its token, or a signed-in procedure fired from an anonymous page, is a client
 * defect the server cannot see (CUR-2010). Those stay under the session rules. The page cannot
 * tell the two apart: both answer UNAUTHORIZED on `/public/*`, and a signed-in page such as
 * `/change-org` sits outside the protected prefixes.
 */
export function isCaptureLeftToServer(error: unknown): boolean {
    if (typeof error !== 'object' || error === null || !('data' in error)) return false;
    const { data } = error;
    if (typeof data !== 'object' || data === null || !('clientCapture' in data) || data.clientCapture !== false) {
        return false;
    }
    return !isUnauthorizedTrpcError(error) || ('errorId' in data && data.errorId === CAPTCHA_REFUSAL_ERROR_ID);
}

/**
 * The server's `email.*` app codes: a required email was refused by the provider after every
 * attempt (`EmailDeliveryError`). The message is written for the user, so callers show it plainly
 * instead of the generic server-error panel, which would wrongly promise that nothing changed.
 */
export function isEmailDeliveryError(error: unknown): error is { message: string } {
    if (
        typeof error !== 'object' ||
        error === null ||
        !('data' in error) ||
        !('message' in error) ||
        typeof error.message !== 'string'
    ) {
        return false;
    }
    const { data } = error;
    if (typeof data !== 'object' || data === null || !('errorId' in data)) return false;
    return typeof data.errorId === 'string' && data.errorId.startsWith('email.');
}

function isExpectedContactWriteDenial(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return false;
    }

    const trpcError = error as { data?: { code?: unknown }; message?: unknown };
    return (
        trpcError.data?.code === 'FORBIDDEN' &&
        typeof trpcError.message === 'string' &&
        EXPECTED_CONTACT_WRITE_DENIAL_MESSAGES.has(trpcError.message)
    );
}

const EXPECTED_PASSWORD_RESET_LINK_ERROR_IDS: ReadonlySet<string> = new Set(PASSWORD_RESET_LINK_ERROR_CODES);

/**
 * A used, expired or SSO-account password reset link is a designed outcome the
 * reset form renders itself. The server already throws these with
 * `captureInSentry: false`; the cache handler must not re-report them
 * (CURIUM-FE-TV).
 */
function isExpectedPasswordResetLinkFailure(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return false;
    }

    const trpcError = error as { data?: { code?: unknown; errorId?: unknown } };
    return (
        trpcError.data?.code === 'BAD_REQUEST' &&
        typeof trpcError.data.errorId === 'string' &&
        EXPECTED_PASSWORD_RESET_LINK_ERROR_IDS.has(trpcError.data.errorId)
    );
}

/** The tRPC code each reserve-change refusal is thrown with; `satisfies` keeps the list complete. */
const RESERVE_CHANGE_REFUSAL_CODES = {
    'claimAuthority.noLevel': 'PRECONDITION_FAILED',
    'claimAuthority.changePending': 'CONFLICT',
} as const satisfies Record<(typeof CLAIM_AUTHORITY_RESERVE_CHANGE_ERROR_CODES)[number], string>;

/** The tRPC code each refusal of a reserve-change decision or re-send is thrown with (CUR-1642, CUR-1643). */
const RESERVE_CHANGE_DECISION_REFUSAL_CODES = {
    'claimAuthority.alreadyDecided': 'CONFLICT',
    'claimAuthority.notAssignee': 'FORBIDDEN',
    'claimAuthority.ownRequest': 'FORBIDDEN',
    'claimAuthority.needsInsurer': 'PRECONDITION_FAILED',
    'claimAuthority.notReferral': 'PRECONDITION_FAILED',
    'claimAuthority.claimClosed': 'CONFLICT',
    'claimAuthority.nobodyToResend': 'PRECONDITION_FAILED',
} as const satisfies Record<(typeof CLAIM_AUTHORITY_DECISION_ERROR_CODES)[number], string>;

/** Refusals under claims authority, keyed by error id, with the tRPC code each is thrown with. */
const EXPECTED_CLAIM_AUTHORITY_REFUSALS: ReadonlyMap<string, string> = new Map([
    ...CLAIM_AUTHORITY_MANAGER_ERROR_CODES.map((id) => [id, 'BAD_REQUEST'] as const),
    ...Object.entries(RESERVE_CHANGE_REFUSAL_CODES),
    ...Object.entries(RESERVE_CHANGE_DECISION_REFUSAL_CODES),
]);

/**
 * A refused manager on a person's claims authority (CUR-1633), a reserve change refused
 * under enforcement (CUR-1641), or a refused decision on a held change (CUR-1642), is a
 * designed outcome the form shows. The reporting-loop and
 * pending-change messages name people, so the server throws them with `captureInSentry: false`
 * and the cache handler must not send them either.
 */
function isExpectedClaimAuthorityRefusal(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return false;
    }

    const trpcError = error as { data?: { code?: unknown; errorId?: unknown } };
    return (
        typeof trpcError.data?.errorId === 'string' &&
        EXPECTED_CLAIM_AUTHORITY_REFUSALS.get(trpcError.data.errorId) === trpcError.data.code
    );
}

/**
 * A paid Ravin run refused over a Clear21 repair quote the handler had not yet
 * acknowledged (CUR-1237) is a designed outcome: the form shows the quote and
 * asks again. The server throws it with `captureInSentry: false` too.
 */
function isExpectedExistingRepairQuoteRefusal(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return false;
    }

    const trpcError = error as { data?: { code?: unknown; errorId?: unknown } };
    return (
        trpcError.data?.code === 'PRECONDITION_FAILED' &&
        trpcError.data.errorId === 'vehicleInspection.existingRepairQuote'
    );
}

/** The tRPC code each expected organisation-setting refusal is thrown with. */
const EXPECTED_ORG_SETTING_REFUSALS: ReadonlyMap<string, string> = new Map([
    ['orgSetting.stale', 'CONFLICT'],
    ['orgSetting.invalidValue', 'BAD_REQUEST'],
]);

/**
 * A control-centre setting save or reset refused because someone else saved first, or because
 * the value fails the setting's rules, is a designed outcome: the editor offers a reload or shows
 * the problem. The server throws both with `captureInSentry: false`; the cache handler must not
 * send them either. The id and code are the contract, so an unexpected fault still reaches Sentry.
 */
function isExpectedOrgSettingRefusal(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return false;
    }

    const trpcError = error as { data?: { code?: unknown; errorId?: unknown } };
    return (
        typeof trpcError.data?.errorId === 'string' &&
        EXPECTED_ORG_SETTING_REFUSALS.get(trpcError.data.errorId) === trpcError.data.code
    );
}

/**
 * Publishing a new obligation version while an older version of it is still awaiting review or in
 * draft (CUR-2023) is a designed outcome: the message names the older version and the page shows
 * it. The server throws it with `captureInSentry: false` too.
 */
function isExpectedObligationPublishRefusal(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return false;
    }

    const trpcError = error as { data?: { code?: unknown; errorId?: unknown } };
    return (
        trpcError.data?.code === 'PRECONDITION_FAILED' &&
        trpcError.data.errorId === 'obligation.olderVersionUnpublished'
    );
}

/**
 * An interactive AI action the server refused because personal information could not be replaced
 * before the provider call (CUR-1730, pre-send sanitisation fails closed). The route throws it as
 * `PRECONDITION_FAILED` with the app code `ai.temporarilyUnavailable`; the code pair, not the
 * message, is the contract. Surfaces that run AI show "AI is temporarily unavailable" for it and
 * keep their generic failure copy for everything else.
 */
export function isAiTemporarilyUnavailableError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return false;
    }

    const trpcError = error as { data?: { code?: unknown; errorId?: unknown } };
    return trpcError.data?.code === 'PRECONDITION_FAILED' && trpcError.data.errorId === 'ai.temporarilyUnavailable';
}

/**
 * True when the error is the well-known "session expired" signal we expect on
 * auth routes after a token times out, or the synthetic one produced by
 * `authTokenCheckLink`. These are normal user-inactivity events — they should
 * not pop a modal and should not be captured to Sentry.
 */
export function isExpectedSessionExpiration(error: unknown, session: AuthSessionSnapshot): boolean {
    // A deliberate logout clears the token before the Auth0 redirect lands, so an
    // UNAUTHORIZED (or the link's synthetic refusal) in that window is its fallout, not
    // a defect. `logout()` keeps `authTokenExpired` false on purpose, so check this first.
    if (session.isLoggingOut && (isSyntheticSessionExpiredError(error) || isUnauthorizedTrpcError(error))) {
        return true;
    }

    if (isSyntheticSessionExpiredError(error) && session.authTokenExpired) {
        return true;
    }

    if (!isUnauthorizedTrpcError(error)) {
        return false;
    }

    return hasMessage(error, 'Session expired') && session.authTokenExpired && isAuthRoute();
}

/**
 * True while a session expiry is in flight and this error is just its fallout:
 * an UNAUTHORIZED response arriving between the token lapsing and `RequireAuth`
 * redirecting. The retired `TRPCErrorBoundary` suppressed exactly this window so
 * the screen stayed quiet; `InlineQueryError` now owns the same rule, otherwise
 * every converted panel would paint "Something went wrong" during a normal
 * logout-by-inactivity.
 *
 * Scoped to protected routes, because only those hand control to `RequireAuth`.
 * A stale `authTokenExpired` flag persists in localStorage after a signed-in
 * session lapses, so on `/public/*` and other anonymous flows an UNAUTHORIZED
 * response is a real, user-visible failure — a rejected CAPTCHA challenge, say —
 * whose panel and retry must stay on screen.
 */
export function isSessionExpiryFallout(error: unknown, session: AuthSessionSnapshot): boolean {
    if (!isProtectedRoute(window.location.pathname)) {
        return false;
    }

    if (isExpectedSessionExpiration(error, session)) {
        return true;
    }

    if (!isUnauthorizedTrpcError(error)) {
        return false;
    }

    return session.authTokenExpired || session.isTokenExpired();
}

/** tRPC error codes for which re-issuing the identical request can never succeed. */
const NON_RETRYABLE_TRPC_CODES: ReadonlySet<string> = new Set(['FORBIDDEN', 'UNAUTHORIZED', 'NOT_FOUND']);

/**
 * True unless the error carries a tRPC code that denotes a stable denial
 * (authorisation / existence), where an identical retry is guaranteed to fail
 * the same way. `InlineQueryError` uses this to suppress its "Try again"
 * affordance in one place instead of at every call site.
 */
export function isRetryableQueryError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return true;
    }

    const trpcError = error as { data?: { code?: unknown } };
    return !(typeof trpcError.data?.code === 'string' && NON_RETRYABLE_TRPC_CODES.has(trpcError.data.code));
}

/**
 * True when a failed query must replace its content instead of leaving cached
 * data visible. Retryable refresh failures may continue to show cached data,
 * while stable denials always block it.
 */
export function hasBlockingQueryError<T>(error: unknown, data: T | null | undefined): boolean {
    return error != null && (data == null || !isRetryableQueryError(error));
}

/**
 * Decide whether the cache layer should capture this error to Sentry.
 *
 * Returns `false` when:
 * - the error is a known/expected session expiration (handled by RequireAuth);
 * - the server marked the error `clientCapture: false` — a refusal it declines to capture, or any
 *   application `NOT_FOUND` — unless it is an UNAUTHORIZED other than a refused captcha challenge;
 * - the error is an expected contact create/update business denial;
 * - the error is a `recordNotFound` denial (the server already dropped it and
 *   kept the Axiom signal);
 * - the error is an expected password reset link outcome (used, expired or SSO account);
 * - the error is a claims-authority refusal (a manager loop, an ineligible person, no level,
 *   a pending reserve change), whose message may name people;
 * - the error is an organisation-setting save refused as stale or invalid;
 * - the error is a new obligation version refused because an older version was never published;
 * - Cloudflare stopped the request with a browser challenge before it reached the API;
 * - the error is a network failure (the user is likely offline — captured
 *   would be noise);
 * - the declared `errorSurface` has a downstream Sentry owner (`route`,
 *   `boundary`), so capturing in the cache would double-fire.
 */
export function shouldCaptureToSentry(
    error: unknown,
    surface: ErrorSurface = DEFAULT_QUERY_ERROR_SURFACE,
    session: AuthSessionSnapshot,
): boolean {
    if (SURFACES_WITH_DOWNSTREAM_SENTRY_OWNER.has(surface)) {
        return false;
    }

    if (isExpectedSessionExpiration(error, session)) {
        return false;
    }

    if (isCaptureLeftToServer(error)) {
        return false;
    }

    if (isExpectedContactWriteDenial(error)) {
        return false;
    }

    if (isRecordNotFoundError(error)) {
        return false;
    }

    if (isExpectedPasswordResetLinkFailure(error)) {
        return false;
    }

    if (isExpectedClaimAuthorityRefusal(error)) {
        return false;
    }

    if (isExpectedExistingRepairQuoteRefusal(error)) {
        return false;
    }

    if (isExpectedOrgSettingRefusal(error)) {
        return false;
    }

    if (isExpectedObligationPublishRefusal(error)) {
        return false;
    }

    // The server already declines to capture it, and one event per click during an analyser
    // outage would only add noise; the blocked-call log and metric carry the signal.
    if (isAiTemporarilyUnavailableError(error)) {
        return false;
    }

    // The server captures one correlated event for the entire expired HTTP batch.
    if (isTrpcExecutionDeadlineError(error)) return false;

    // Cloudflare's bot protection stopped the request at the edge; the API never saw it.
    // Nearly all of these are automated clients (CURIUM-FE-RE), and the panel tells a
    // person to reload. Edge error pages that are not challenges are still captured, and the
    // cache layer records a sampled warning for challenges so real-user impact stays visible.
    if (isEdgeChallengeError(error)) return false;

    if (isNetworkError(error)) {
        return false;
    }

    return true;
}
